import { TemplatedConfigurationRunDrawer } from "#src/batteries-included-components/Drawers/TemplatedConfigurationRunDrawer";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { Column, Link, Row, StorageKeys } from "@validereinc/common-components";
import { type FlowType } from "@validereinc/domain";
import { useFetchResourceDependencyGraph } from "#src/components/hooks/adapters/useFetchResourceDependencyGraph";
import classNames from "classnames/bind";
import React, { ReactNode, useState } from "react";
import { FlowUpdateStatusBanner } from "../FlowUpdateStatusBanner";
import styles from "./FlowDetailPage.module.scss";
import { FlowDetailsPanel } from "./FlowDetailsPanel";
import { FlowDevicesPanel } from "./FlowDevicesPanel";
import { FlowDownstreamPanel } from "./FlowDownstreamPanel";
import { FlowUpstreamPanel } from "./FlowUpstreamPanel";

const cx = classNames.bind(styles);

export const FlowOverviewTab = ({
  flow,
  isLoading,
  viewFilterAreaSlot,
}: Pick<StorageKeys, "viewConfigStorageKey"> & {
  viewFilterAreaSlot: ReactNode;
  flow?: FlowType;
  isLoading: boolean;
}) => {
  if (!flow?.id) {
    return null;
  }

  const [isTemplatedConfigsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:templated_configurations",
    permissionQuery: "templated_configurations:read",
  });

  const templatedConfigurationRunDependencyGraphQuery =
    useFetchResourceDependencyGraph(
      { id: flow.id },
      { enabled: isTemplatedConfigsAvailable }
    );

  const [
    isTemplatedConfigurationRunDrawerOpen,
    setIsTemplatedConfigurationRunDrawerOpen,
  ] = useState(false);

  const templatedConfigurationRunDependencyGraph =
    templatedConfigurationRunDependencyGraphQuery?.data?.data;

  const templatedConfigurationLink =
    templatedConfigurationRunDependencyGraph?.name ? (
      <Link
        onClick={() => setIsTemplatedConfigurationRunDrawerOpen(true)}
        label={templatedConfigurationRunDependencyGraph.name}
      />
    ) : undefined;

  return (
    <>
      <FlowUpdateStatusBanner />
      {viewFilterAreaSlot}
      <div className={cx("container")}>
        <div className={cx("contentContainer")}>
          <FlowDetailsPanel
            flow={flow}
            isLoading={isLoading}
            templatedConfigurationLink={templatedConfigurationLink}
          />
          <Row>
            <Column variant={8}>
              <FlowUpstreamPanel
                flow={flow}
                isLoading={isLoading}
              />
            </Column>

            <Column variant={8}>
              <FlowDownstreamPanel
                flow={flow}
                isLoading={isLoading}
              />
            </Column>
          </Row>

          <FlowDevicesPanel
            flowId={flow?.id}
            isLoading={isLoading}
          />
        </div>
      </div>
      <IsFeatureAvailable
        featureFlagQuery="core:templated_configurations"
        permissionQuery="templated_configurations:read"
        fallbackChildren={null}
      >
        <TemplatedConfigurationRunDrawer
          isOpen={isTemplatedConfigurationRunDrawerOpen}
          onClose={() => setIsTemplatedConfigurationRunDrawerOpen(false)}
          templatedConfigurationRunDependencyGraph={
            templatedConfigurationRunDependencyGraph
          }
          resourceId={flow.id}
        />
      </IsFeatureAvailable>
    </>
  );
};
